import React, { useState } from 'react';
import { TextField } from '@mui/material';

import { FilterRowSelection } from '@lambda-layer/types/filter.ts';

interface Props {
  filterRowSelection: FilterRowSelection;
  onChange: (filterRowValue: FilterRowSelection) => void;
  onFocusChange?: () => void;
  disabled?: boolean;
  isNumeric?: boolean;
  autoFocus?: boolean;
}

const FilterRowTextField: React.FC<Props> = ({
  filterRowSelection,
  onChange,
  onFocusChange,
  disabled = false,
  isNumeric = false,
  autoFocus = false,
}) => {
  // Track whether the input is focused to determine if formatting should be applied
  const [isFocused, setIsFocused] = useState(false);

  const handleTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let { value } = event.target;
    if (isNumeric) {
      // Remove commas so that the value can be properly parsed as a number
      const cleanedValue = value.replace(/,/g, '');
      // Only update if the cleaned value is a valid number (or empty)
      if (cleanedValue && isNaN(Number(cleanedValue))) {
        return;
      }
      value = cleanedValue;
    }
    const updatedValue = {
      ...filterRowSelection,
      value: value ? [value] : [],
    };
    onChange(updatedValue);
  };

  const handleFocus = () => {
    setIsFocused(true);
    if (onFocusChange) {
      onFocusChange();
    }
  };

  const handleBlur = () => {
    setIsFocused(false);
    if (onFocusChange) {
      onFocusChange();
    }
  };

  // Get the raw value from filterRowSelection
  const rawValue = filterRowSelection.value[0] || '';
  // When not focused and if it's numeric, format the value with commas
  let displayValue = rawValue;
  if (isNumeric && !isFocused && rawValue) {
    const numericValue = Number(rawValue.toString().replace(/,/g, ''));
    if (!isNaN(numericValue)) {
      displayValue = numericValue.toLocaleString();
    }
  }

  return (
    <TextField
      autoFocus={autoFocus}
      value={displayValue}
      onChange={handleTextChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      size="small"
      disabled={disabled}
    />
  );
};

export default FilterRowTextField;
